<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Monthly Order Report Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyOrderReportFilter"
              @reset="resetOrderReportFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingOrderReportFilter.startDate"
                  :endDate="editingOrderReportFilter.endDate"
                  @updateStartDate="editingOrderReportFilter.startDate = $event"
                  @updateEndDate="editingOrderReportFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedOrderReportFilter.startDate | formatDate}} - {{appliedOrderReportFilter.endDate | formatDate}}
                </div>
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Operational DIFOT" highlight>
              {{formatPercent(averageOperationalPercent, 1)}}
              <template v-slot:info>{{orderSummaryData.totalHitCount}} orders hit and {{orderSummaryData.totalMissCount}} orders missed</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Commercial DIFOT">
              {{formatPercent(averageCommercialPercent, 1)}}
              <template v-slot:info>{{orderSummaryData.totalHitCount}} orders hit over {{orderSummaryData.totalOrderCount}} total orders</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartLine
          title="Monthly Operational Report"
          :series="orderMonthlyReportData.series"
          :xAxis="orderMonthlyReportData.xAxis"
          :yAxis="orderMonthlyReportData.yAxis"
          :legend="orderMonthlyReportData.legend"
          :options="orderMonthlyReportData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartLine from '@/components/charts/chartLine'

export default {
  name: 'OrderDIFOTMonthlyOperationalWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartLine,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      orderSummaryData: {
        totalOrderCount: 0,
        totalHitCount: 0,
        totalMissCount: 0,
        totalExcludeCount: 0
      },
      orderMonthlyReportData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultOrderReportFilter: {    
        startDate: moment().local().subtract(6, 'months').startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingOrderReportFilter: {},
      appliedOrderReportFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
    averageOperationalPercent() {
      var totalValid = this.orderSummaryData.totalHitCount + this.orderSummaryData.totalMissCount
      if(totalValid > 0)
        return (this.orderSummaryData.totalHitCount / totalValid) * 100.0
      return 0
    },
    averageCommercialPercent() {
      if(this.orderSummaryData.totalOrderCount > 0)
        return (this.orderSummaryData.totalHitCount / this.orderSummaryData.totalOrderCount) * 100.0
      return 0
    }
  },
  mounted() {
    this.editingOrderReportFilter = _.clone(this.defaultOrderReportFilter)
    this.appliedOrderReportFilter = _.clone(this.defaultOrderReportFilter)

    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyOrderReportFilter() {
      this.appliedOrderReportFilter = _.clone(this.editingOrderReportFilter)

      this.requestChartData()
    },
    resetOrderReportFilter() {
      this.editingOrderReportFilter = _.clone(this.defaultOrderReportFilter)
      this.appliedOrderReportFilter = _.clone(this.defaultOrderReportFilter)

      this.requestChartData()
    },
    requestChartData() {
      var orderReportFilter = {
        type: 'monthly',
        startDate: this.appliedOrderReportFilter.startDate,
        endDate: this.appliedOrderReportFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      const widgetComp = this
      Promise.all([
        this.$apiService.analyticsService.getOrderDIFOTChartData(orderReportFilter),
        this.$apiService.analyticsService.getOrderDIFOTSummary(orderReportFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        
        let reportingSummary = results[1].data.reportingSummary || {}
        this.orderSummaryData.totalOrderCount = parseInt(reportingSummary.totalOrderCount || 0)
        this.orderSummaryData.totalHitCount = parseInt(reportingSummary.totalHitCount || 0)
        this.orderSummaryData.totalMissCount = parseInt(reportingSummary.totalMissCount || 0)
        this.orderSummaryData.totalExcludeCount = parseInt(reportingSummary.totalExcludeCount || 0)

        let seriesList = []

        // Format scattered data into chart data
        if(chartData.length > 0) {
          var seriesData = chartData.map(chartEntry => {
            let opScore = 0
            let commScore = 0
            let totalValidOrders = chartEntry.hitOrderCount + chartEntry.missOrderCount
            if(totalValidOrders > 0)
              opScore = (chartEntry.hitOrderCount / totalValidOrders) * 100.0
            if(chartEntry.totalOrderCount > 0)
              commScore = (chartEntry.hitOrderCount / chartEntry.totalOrderCount) * 100.0

            return {
              opValue: opScore,
              commValue: commScore,
              date: moment(chartEntry.date).format('MMM YYYY')
            }
          })

          var targetPercent = parseFloat(chartData[0].operationalReportingTargetPercent || 0)

          seriesList.push({
            name: `Operational DIFOT`,
            type: 'line',
            data: seriesData.map(chartEntry => chartEntry.opValue),
            tooltip: {
              valueFormatter: (value) => {
                return widgetComp.formatPercent(value);
              }
            }
          })
          seriesList.push({
            name: `Commercial DIFOT`,
            type: 'line',
            data: seriesData.map(chartEntry => chartEntry.commValue),
            tooltip: {
              valueFormatter: (value) => {
                return widgetComp.formatPercent(value);
              }
            },
            markLine: {
              name: 'Target',
              data: [{ yAxis: targetPercent }],
              lineStyle: {
                type: 'dashed',
                color: 'red'
              },
              label: {
                show: true,
                formatter: 'Target: ' + this.formatPercent(targetPercent, 1)
              }
            },
          })
        }

        this.orderMonthlyReportData.legend = {
          data: ['Operational DIFOT', 'Commercial DIFOT']
        }
        this.orderMonthlyReportData.xAxis = {
          type: 'category',
          data: seriesData.map(chartEntry => chartEntry.date)
        }
        this.orderMonthlyReportData.yAxis = [
          {
            type: 'value',
            min: 0,
            max: 100,
            axisLabel: {
              formatter: (value) => {
                return this.formatPercent(value, 0)
              }
            }
          }
        ]
        this.orderMonthlyReportData.series = seriesList
      })
    },
  }
}
</script>